import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "@env";
import { Observable } from "rxjs";
import { map, catchError } from "rxjs/operators";

import { ErrorHandlingService } from "src/app/features/ada-access/shared/errorHandling/errorHandling.service";
import { API_ENDPOINTS } from "../../secure-blockchain-api-endpoints";
import { FabricChaincode } from "@aptsi-types";

@Injectable({
  providedIn: "root"
})
export class SmartContractService {

  private baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService
  ) { }

  // ---------------------------------------------------------
  // GET LIST
  // ---------------------------------------------------------

  getContracts(
    organizationId: string | null
  ): Observable<FabricChaincode[]> {

    return this.http.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.VIEW.READ}`,
      { organizationId }
    ).pipe(
      map(res => {
        const key = Object.keys(res ?? {})[0];
        return (res?.[key] ?? []) as FabricChaincode[];
      }),
      catchError(err => this.errorHandlingService.handleError(err))
    );
  }

  // ---------------------------------------------------------
  // GET BY ID
  // ---------------------------------------------------------

  getContractById(
    id: string
  ): Observable<FabricChaincode | null> {

    return this.http.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.OPERATION.READ}`,
      { chaincodeId: id }
    ).pipe(
      map(res =>
        (res?.["Resource retrieved successfully"]?.[0] as FabricChaincode)
        ?? null
      ),
      catchError(err => this.errorHandlingService.handleError(err))
    );
  }

  // ---------------------------------------------------------
  // SEARCH
  // ---------------------------------------------------------

  searchContracts(
    currentPage: number,
    searchText: string,
    category: string,
    organizationId: string | null
  ): Observable<FabricChaincode[]> {

    return this.http.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.OPERATION.SEARCH}`,
      {
        currentPage,
        searchText,
        category,
        organizationId
      }
    ).pipe(
      map(res => (res?.chaincodes ?? res) as FabricChaincode[]),
      catchError(err => this.errorHandlingService.handleError(err))
    );
  }

  // ---------------------------------------------------------
  // CREATE / DEPLOY
  // ---------------------------------------------------------

  createContract(
    file: File,
    data: any
  ): Observable<any> {

    const formData = new FormData();

    formData.append("file", file);
    formData.append("dto", JSON.stringify(data));

    return this.http.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.CONTRACT.DEPLOY}`,
      formData
    ).pipe(
      catchError(err => this.errorHandlingService.handleError(err))
    );
  }

  // ---------------------------------------------------------
  // UPDATE
  // ---------------------------------------------------------

  updateContract(
    file: File | null,
    data: any
  ): Observable<any> {

    const formData = new FormData();

    if (file) {
      formData.append("file", file);
    }

    formData.append("dto", JSON.stringify(data));

    /*
     * Configure API_ENDPOINTS.CONTRACT.UPDATE to point to the
     * Express route that proxies to the Spring Boot
     * /FabricChaincodeService/chaincode/update endpoint.
     *
     * Do not use DEPLOY here.
     */
    return this.http.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.CONTRACT.UPDATE}`,
      formData
    ).pipe(
      catchError(err => this.errorHandlingService.handleError(err))
    );
  }

  // ---------------------------------------------------------
  // JOB STATUS
  // ---------------------------------------------------------

  getStatus(jobId: string): Observable<any> {

    if (!jobId || !jobId.trim()) {
      throw new Error("A valid jobId is required to retrieve status");
    }

    return this.http.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.CONTRACT.STATUS}`,
      { jobId }
    ).pipe(
      catchError(err => this.errorHandlingService.handleError(err))
    );
  }

  // ---------------------------------------------------------
  // DELETE
  // ---------------------------------------------------------

  deleteContract(data: any): Observable<any> {

    return this.http.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.CONTRACT.DELETE}`,
      data
    ).pipe(
      catchError(err => this.errorHandlingService.handleError(err))
    );
  }
}